import { data } from 'jquery';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { map, takeUntil } from 'rxjs/operators';
import { ProcessTypeDto } from 'src/app/_models/ProcessTypeDto';
import { EmployeeResultProcessDto } from 'src/app/_models/dto/EmployeeProcessStatusDto';
import { BackgroundJobExecutionLogDto } from 'src/app/_models/dto/backgroundJobExecutionLogDto';
import { ClientProcessDto } from 'src/app/_models/dto/client-process';
import { ProcessDto } from 'src/app/_models/dto/process-dto';
import { ProcessInputRequestDto } from 'src/app/_models/dto/process-input-request-dto';
import { ProcessStatusCount } from 'src/app/_models/dto/process-status-count';
import { FrequencyType } from 'src/app/_models/frequencyType';
import { PaginatedResult } from 'src/app/_models/pagination/pagination';
import { ProcessScheduleDto } from 'src/app/_models/processSchedulerDto';
import { SchedulerLogDto } from 'src/app/_models/schedulerLogDto';
import { environment } from 'src/environments/environment';

const httpOptions = {
  headers: new HttpHeaders(
    {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  )
};

@Injectable({
  providedIn: 'root'
})
export class ProcessManagementService {
  baseAPI = environment.baseApiUrl;
  private cancelForClient$ = new Subject<void>();
  private cancelForClientOrUser$ = new Subject<void>();
  private cancelForProcessType$ = new Subject<void>();
  private cancelForUserProcess$ = new Subject<void>();
  private cancelIsProcessRunningForUser$ = new Subject<void>();

  constructor(private http: HttpClient) { }

  getUserAllProcesses(userId: number) {
    this.cancelForUserProcess$.next();
    return this.http.get(this.baseAPI + 'Process/userProcess', { params: { UserId: userId.toString() }, headers: httpOptions.headers }).pipe(
      takeUntil(this.cancelForUserProcess$)
    )
  }
  isProcessRunningForUser(userId: number) {
    this.cancelIsProcessRunningForUser$.next();
    return this.http.get(this.baseAPI + 'Process/IsProcessRunning/' + userId + '/user', { headers: httpOptions.headers }).pipe(
      takeUntil(this.cancelIsProcessRunningForUser$)
    );
  }
  async setProcess(processData: ProcessDto): Promise<boolean> {
    return await this.http.post<boolean>(this.baseAPI + 'Process/setProcess', processData, { headers: httpOptions.headers }).toPromise();
  }
  async updateProcess(processInputRequestDto: ProcessInputRequestDto): Promise<boolean> {
    return await this.http.post<boolean>(this.baseAPI + 'Process/updateProcess', processInputRequestDto, { headers: httpOptions.headers }).toPromise();
  }

  async getEmployeeProcessStatus(clientId: number): Promise<EmployeeResultProcessDto> {
    return await this.http.get<EmployeeResultProcessDto>(this.baseAPI + 'Process/RunningProcess/' + clientId + '/client', { headers: httpOptions.headers }).toPromise();
  }

  isProcessRunningForClientOrUser(clientId?: number, userId?: number) {
    this.cancelForClientOrUser$.next();
    return this.http.get(this.baseAPI + 'Process/IsProcessRunning/' + clientId + '/client/' + userId + '/user', { headers: httpOptions.headers }).pipe(
      takeUntil(this.cancelForClientOrUser$) // Ensures only the latest request is taken
    );
  }
  isProcessRunningForClient(clientId: number) {

    this.cancelForClient$.next();
    return this.http.get(this.baseAPI + 'Process/IsProcessRunning/' + clientId + '/client', { headers: httpOptions.headers }).pipe(
      takeUntil(this.cancelForClient$)
    );
  }
  isProcessRunning(clientId: number, processTypeId: number) {
    this.cancelForProcessType$.next();
    return this.http.get(this.baseAPI + 'Process/IsProcessRunning/' + clientId + '/client/' + processTypeId + '/type', { headers: httpOptions.headers }).pipe(
      takeUntil(this.cancelForProcessType$)
    );
  }


  uploadResultFile(result: any) {
    return this.http.post(this.baseAPI + 'FileUpload/result', result);
  }

  getClientProcesses(
    clientId: number,
    status: number,
    searchKey?,
    page?,
    itemPerPage?,

  ) {
    const paginatedResult: PaginatedResult<ClientProcessDto[]> = new PaginatedResult<ClientProcessDto[]>();

    let params = new HttpParams();

    if (searchKey != null) {
      params = params.append("searchKey", searchKey);
    }
    if (page != null && itemPerPage != null) {
      params = params.append("pageNumber", page);
      params = params.append("pageSize", itemPerPage);
    }
    if (status != null && status != undefined) {
      params = params.append("status", status.toString());
    }

    return this.http
      .get<ClientProcessDto[]>(this.baseAPI + `processes/client/${clientId}`, {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          paginatedResult.result = response.body;
          if (response.headers.get("Pagination") != null) {
            paginatedResult.pagination = JSON.parse(response.headers.get("Pagination"));
          }
          return paginatedResult;
        })
      );
  }

  getProcessStatusCount(clientId: number) {
    return this.http.get<ProcessStatusCount[]>(this.baseAPI + 'processes/status/client/' + clientId, { headers: httpOptions.headers });
  }

  cancelProcess(processId: number): Observable<any> {
    return this.http.post(`${this.baseAPI}Process/CancelProcess/${processId}`, {});
  }
  getProcessInfoById(processId: number): Observable<BackgroundJobExecutionLogDto[]> {
    return this.http.get<BackgroundJobExecutionLogDto[]>(this.baseAPI + 'processDetails/' + processId, {})
  }
  getPaginatedSchedulerLogs(params: HttpParams) {
    const paginatedResult: PaginatedResult<SchedulerLogDto[]> = new PaginatedResult<SchedulerLogDto[]>();


    return this.http
      .get<SchedulerLogDto[]>(this.baseAPI + "ProcessSchedules/logs", {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          paginatedResult.result = response.body;
          if (response.headers.get("Pagination") != null) {
            paginatedResult.pagination = JSON.parse(
              response.headers.get("Pagination")
            );
          }
          return paginatedResult;
        })
      );
  }
  GetProcessSchedule(params: HttpParams) {
    const paginatedResult: PaginatedResult<ProcessScheduleDto[]> = new PaginatedResult<ProcessScheduleDto[]>();


    return this.http
      .get<ProcessScheduleDto[]>(this.baseAPI + "ProcessSchedules", {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          paginatedResult.result = response.body;
          if (response.headers.get("Pagination") != null) {
            paginatedResult.pagination = JSON.parse(
              response.headers.get("Pagination")
            );
          }
          return paginatedResult;
        })
      );
  }

  getProcessTypes() {
    return this.http.get<ProcessTypeDto[]>(this.baseAPI + 'ProcessSchedules/process-types', { headers: httpOptions.headers });
  }
  getFrequencyTypes() {
    return this.http.get<FrequencyType[]>(this.baseAPI + 'ProcessSchedules/frequency-types', { headers: httpOptions.headers });
  }
  getSchedulerById(schedulerId: number): Observable<ProcessScheduleDto> {
    
    let params = new HttpParams();
    params = params.append('id', schedulerId);
    params = params.append('timeZoneId', Intl.DateTimeFormat().resolvedOptions().timeZone);
    return this.http.get<ProcessScheduleDto>(`${this.baseAPI}ProcessSchedules/details`, {params, headers: httpOptions.headers });
  }
  addScheduler(data: ProcessScheduleDto): Observable<any> {
    return this.http.post<any>(`${this.baseAPI}ProcessSchedules`, data, { headers: httpOptions.headers });

  }
  UpdateScheduler(data: ProcessScheduleDto, id: number): Observable<any> {
    return this.http.put<any>(`${this.baseAPI}ProcessSchedules/${id}`, data, { headers: httpOptions.headers });

  }
}
